import cCvcHint from '#themes/default/default/components/cvcHint/cvcHint.js';
import cDropdownItem from '#themes/default/default/components/dropdownItem/dropdownItem.js';
import cFormLine from '#themes/default/default/components/formLine/formLine.js';
import cSkeleton from '#themes/default/default/components/skeleton/skeleton.js';
import cSvg from '#themes/default/default/components/svg/svg.js';

import template from './dropdown.html' with { type: 'html' };

export default {
	name: 'CDropdown',
	components: {
		cCvcHint,
		cDropdownItem,
		cFormLine,
		cSkeleton,
		cSvg
	},
	props: {
		customer: {
			type: Object,
			default: () => null
		},
		errorMessage: {
			type: String,
			default: ''
		},
		hasErrorFromParent: {
			type: Boolean,
			default: false
		},
		isCvcFocused: {
			type: Boolean,
			default: false
		},
		note: {
			type: String,
			default: ''
		},
		order: {
			type: Object,
			default: () => null
		},
		payment: {
			type: Object,
			default: () => null
		},
		selectedPayment: {
			type: Object,
			default: () => null
		},
		theme: {
			type: Object,
			default: () => null
		},
		url: {
			type: Object,
			default: () => null
		},
		utils: {
			type: Object,
			default: () => null
		}
	},
	data() {
		return {
			isOpen: false,
			focusedOption: this.payment.providerAccountPaymentOptions[0]
		};
	},
	computed: {
		paymentOptions() {
			let paymentOptions = this.payment.providerAccountPaymentOptions.slice(0, 3);
			// Hotfix: Remove PayPal from the payment options for 1-click trial product flow (CART-4866)
			if (this.order.hasTrialProduct) {
				paymentOptions = paymentOptions.filter((paymentOption) => paymentOption.type !== 'payPalBilling');
				if (!paymentOptions.length) {
					this.utils.showLoader();
					if (this.payment.isHPPReady) {
						// Force the new method flow if there isn't any other payment method after paypal filtration
						this.addPaymentMethod();
					}
				} else {
					// Select/Focus another available payment method
					this.$emit('select', paymentOptions[0]);
					this.focusedOption = this.selectedPayment;
				}
			}
			return paymentOptions;
		}
	},
	watch: {
		selectedPayment() {
			const selectedOption = this.paymentOptions.find((paymentOption) => paymentOption.sourceId === this.selectedPayment.sourceId);
			this.focusedOption = selectedOption ?? null;
		}
	},
	methods: {
		addPaymentMethod() {
			this.utils.showLoader();
			this.$store.commit('SET_SELECTED_PAYMENT', {
				cardBrand: null,
				cardcvv: null,
				cardexpiration: null,
				cardnumber: null,
				iban: null,
				sourceClientSecret: null,
				sourceId: null
			});
			this.$store.commit('SET_PAYMENT', {
				...this.$store.state.payment,
				sourceClientSecret: null,
				sourceId: null
			});
			this.$store.commit('SET_NEW_METHOD', true);
			// Display label even after multiple visits
			this.$store.commit('SET_FORM', {
				creditCard: null,
				directDebit: null
			});
			// Reset field that are currently hidden in the billing form to prevent mismatch
			this.$store.commit('SET_CUSTOMER', {
				...this.customer,
				billing: {
					...this.customer.billing,
					line1: '',
					line2: '',
					city: '',
					companyName: '',
					phoneNumber: '',
					state: ''
				}
			});
			this.utils.hideLoader();
		},
		openAndFocusDropdown() {
			this.isOpen = true;
			this.$nextTick(() => {
				this.$refs.dropdownOptionsList.focus();
			});
		},
		closeAndResetDropdown() {
			this.focusedOption = this.selectedPayment; // Reset focused option
			this.isOpen = false;
			this.$nextTick(() => {
				this.$refs.dropdownClosed.focus();
			});
		},
		onClickToggle() {
			if (this.isOpen) {
				this.closeAndResetDropdown();
			} else {
				this.openAndFocusDropdown();
			}
		},
		onKeyDownToggle(event) {
			if ([' ', 'Enter', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
				this.openAndFocusDropdown();
			} else if (['Escape'].includes(event.key)) {
				this.closeAndResetDropdown();
			}
		},
		onTab(event) {
			if (['Tab'].includes(event.key)) {
				this.closeAndResetDropdown();
			}
		},
		onKeyDown(event) {
			const currentIndex = this.paymentOptions.findIndex((option) => option.id === this.focusedOption.id);
			if (['Escape'].includes(event.key)) {
				this.closeAndResetDropdown();
			} else if (event.key === 'Home') {
				this.focusedOption = this.paymentOptions[0];
			} else if (event.key === 'End') {
				this.focusedOption = {
					id: 'addButton'
				};
			} else if (event.key === 'ArrowDown') { // Move focus to the next element
				if (this.focusedOption.id !== 'addButton') {
					this.focusedOption = currentIndex + 1 < this.paymentOptions.length ? this.paymentOptions[currentIndex + 1] : {
						id: 'addButton'
					};
				}
			} else if (event.key === 'ArrowUp') { // Move focus to the previous element
				if (this.focusedOption.id !== 'addButton') {
					this.focusedOption = currentIndex > 0 ? this.paymentOptions[currentIndex - 1] : this.focusedOption;
				} else {
					const lastOption = this.paymentOptions[this.paymentOptions.length - 1];
					this.focusedOption = lastOption;
				}
			} else if ([' ', 'Enter'].includes(event.key)) {
				if (this.focusedOption.id === 'addButton') {
					this.addPaymentMethod();
				} else { // Change selected method
					this.$store.commit('SET_SELECTED_PAYMENT', this.focusedOption);
					this.$emit('select', this.focusedOption);
					this.closeAndResetDropdown();
				}
			}
		}
	},
	template
};
